import { useEffect, useState } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { adminApi } from "../../services/adminApi";
import { sentimentAnalyticsApi } from "../../services/sentimentAnalyticsApi";
import { Card } from "../../components/common/Card";
import { Spinner } from "../../components/common/Spinner";
import { StatTile } from "../../components/common/StatTile";
import { GapNotice } from "../../components/common/GapNotice";

export function SentimentAnalysisPage() {
  const [byFeature, setByFeature] = useState(null);
  const [trend, setTrend] = useState(null);
  const [byTheme, setByTheme] = useState(null);
  const [byTier, setByTier] = useState(null);

  useEffect(() => {
    adminApi.sentiment().then((r) => setByFeature(r.sentiment_by_feature)).catch(() => setByFeature([]));
    sentimentAnalyticsApi.weeklyTrend().then((r) => setTrend(r.weeks)).catch(() => setTrend(null));
    sentimentAnalyticsApi.byTheme().then((r) => setByTheme(r.by_theme)).catch(() => setByTheme(null));
    sentimentAnalyticsApi.byTier().then((r) => setByTier(r.by_tier)).catch(() => setByTier(null));
  }, []);

  if (!byFeature) return <Spinner />;

  const overallAvg = byFeature.length
    ? byFeature.reduce((sum, f) => sum + f.avg_rating * f.response_count, 0) / byFeature.reduce((sum, f) => sum + f.response_count, 0)
    : null;

  return (
    <div>
      <h1>User Sentiment Analysis</h1>

      <div className="section-spacing" style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <StatTile label="Aggregated Rating" value={overallAvg ? overallAvg.toFixed(2) : "--"} sublabel="out of 5, all feedback" />
        <StatTile label="Total Responses" value={byFeature.reduce((sum, f) => sum + f.response_count, 0)} />
      </div>

      <Card title="Feature-Specific Sentiment" className="section-spacing">
        {byFeature.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)" }}>No feedback recorded yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byFeature}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="feature_context" angle={-20} textAnchor="end" height={70} />
              <YAxis domain={[0, 5]} />
              <Tooltip />
              <Bar dataKey="avg_rating" fill="#f2a900" name="Avg Rating" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="Sentiment Trend (weekly)" className="section-spacing">
        {trend ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis domain={[0, 5]} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="avg_rating" stroke="#2e8b57" name="Avg Rating" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <GapNotice>
            app.UserFeedback.timestamp already supports a GROUP BY week query -- just needs an endpoint to run it.
          </GapNotice>
        )}
      </Card>

      <div className="section-spacing" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
        <Card title="Sentiment by Theme">
          {byTheme ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byTheme}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="theme_name" />
                <YAxis domain={[0, 5]} />
                <Tooltip />
                <Bar dataKey="avg_rating" fill="#3b82c4" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <GapNotice>Needs a join of UserFeedback.user_id → app.Users.selected_theme, grouped by theme.</GapNotice>
          )}
        </Card>
        <Card title="Sentiment by Subscription Tier">
          {byTier ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byTier}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="subscription_tier" />
                <YAxis domain={[0, 5]} />
                <Tooltip />
                <Bar dataKey="avg_rating" fill="#f2a900" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <GapNotice>Same join, grouped by subscription_tier instead of theme.</GapNotice>
          )}
        </Card>
      </div>

      <Card title="NPS Score, Keyword Cloud & Feedback Response Time">
        <GapNotice>
          NPS needs a dedicated 0-10 "how likely to recommend" question -- app.UserFeedback's rating is a 1-5 star
          scale (CHECK constraint) and can't be converted to a real NPS. Keyword cloud needs text/NLP processing over
          the `comment` column that doesn't run anywhere today. Response time needs a resolved_date column --
          `resolved` is currently a plain BIT with no timestamp to measure against.
        </GapNotice>
      </Card>
    </div>
  );
}
